<template>
  <div ref="component" class="container episode-editor">
    <div v-if="errorMessage" class="alert alert-danger">
      <p>{{ errorMessage }}</p>
    </div>
    <div v-if="successMessage" class="alert alert-success">
      <p>{{ successMessage }}</p>
    </div>
    <div class="header row">
      <div class="title col h1">
        {{ episode.name }}
      </div>
      <div class="button-container col-auto">
        <button class="btn btn-outline-dark float-right" v-on:click="deleteEpisode">
          <fa-icon icon="trash-alt"></fa-icon>
        </button>
        <button class="btn btn-outline-dark float-right" v-on:click="saveEpisode">Save</button>
        <div class="notification">{{ notification }}</div>
      </div>
    </div>
    <div v-if="canPublish" class="row">
      <div class="published col">
        <fa-icon
            :icon="this.episode.published ? 'toggle-on' : 'toggle-off'"
            class="clickable"
            v-on:click="togglePublished"
        ></fa-icon>
        Published
        <autopublish-widget
            :publish-date="episode.publish.publish_date"
            :published="episode.published"
            @autopublish="setAutopublishDate"
        />
      </div>
      <div class="published col">
        <fa-icon
            :icon="this.episode.is_free ? 'toggle-on' : 'toggle-off'"
            class="clickable"
            v-on:click="toggleIsFree"
        ></fa-icon>
        Free Of Charge
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-8">
        <label for="episodeName">Episode name</label>
        <input id="episodeName" v-model="episode.name" class="form-control" type="text"/>
        <div v-if="getErrors('episode.name')" class="text-danger">
          <p v-for="error in getErrors('episode.name')" v-bind:key="error">{{ error }}</p>
        </div>
      </div>
      <div class="form-group col-sm-2 theme-select">
        <label for="episodeTheme">Theme</label>
        <select id="episodeTheme"
                v-model="episode.theme_id"
                class="custom-select"
        >
          <option
              v-for="theme in themes"
              v-bind:key="theme.id"
              v-bind:value="theme.id"
          >{{ theme.name }}
          </option>
        </select>
      </div>

      <div v-if="episode.region_id === 1" class="form-group col-sm-12 theme-select">
        <label for="episodeName">Universal / Deep links</label>
        <input id="universal-link" :value="'https://naudio.app/snicks/'+episode.story.id +'/'+ episode.id"
               class="form-control" disabled type="text"/>
        <input id="deep-link" :value="'naudio://snicks/'+episode.story.id +'/'+ episode.id"
               class="form-control" disabled type="text"/>
      </div>

    </div>

    <!-- Music -->
    <div v-if="showAudioUpload" class="row">
      <music :episodeId="episodeId"/>
    </div>

    <audio-upload
        v-if="showAudioUpload"
        :alertUploadButton="alertUploadButton"
        :compactLayout=false
        :episode="episode"
        :episodeId="episodeId"
        :extensions="extensions"
        :hasAudioFile="hasAudioFile"
        :removeUploadButtonAlert="removeUploadButtonAlert"
        :setErrorMessage="setErrorMessage"
        :setSuccessMessage="setSuccessMessage"
        :snickId="snickId"
    ></audio-upload>

    <template v-if="showEditor">
      <picker
          ref="emojiPicker"
          :style="emojiStyles"
          emoji="point_up_2"
          title="Choose an emoji"
          @select="emoji($event)"
      ></picker>

      <draggable
          v-model="messages"
          :component-data="getDraggableData()"
          :options="{ handle: '.drag-handle' }"
          class="editor"
          element="div"
          @end="drag=false"
          @start="drag=true"
      >
        <message-row
            v-for="(message, index) in messages"
            :key="index"
            ref="messageRow"
            :index="index"
            :message="message"
            :snickId="snickId"
            v-on:input="updateMessage(message, $event)"
            v-on:toggle-emoji-picker="toggleEmojiPicker($event)"
            v-on:toggle-fullscreen-message="toggleFullscreenMessage(index)"
            v-on:change-position="setMessagePosition($event, message)"
            v-on:change-character="setCharacter($event, index)"
            v-on:delete-message="deleteMessage($event)"
            v-on:image-loaded="setImage($event, index)"
        ></message-row>
      </draggable>
      <div class="row">
        <div class="col-sm-auto">
          <button id="new-row" class="btn btn-dark" v-on:click="newEntry">{{ $t('interface.btnNew') }}</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import MessageRow from './MessageRow';
import AudioUpload from './AudioUpload';
import AutopublishWidget from '../AutopublishWidget/AutopublishWidget';
import Music from './Music.vue';
import _ from "lodash";

export default {
  props: {
    init: Object,
    snickId: Number,
    episodeId: Number,
    canPublish: Boolean,
  },
  components: {
    MessageRow,
    draggable,
    AutopublishWidget,
    AudioUpload,
    Music,
  },
  methods: {
    toggleFullscreenMessage(index) {
      this.messages[index].fullscreen = !this.messages[index].fullscreen;
    },
    toggleEmojiPicker(event) {
      if (event.index === this.emojiPicker.index) {
        this.emojiPicker.show = false;
        this.emojiPicker.id = null;
        this.emojiPicker.index = -1;
      } else {
        this.emojiPicker = event;
      }
    },
    emoji(emoji) {
      const index = this.emojiPicker.index;
      this.$refs.messageRow[index].insertEmoji(emoji.native);
      this.toggleEmojiPicker(this.emojiPicker);
    },
    updateMessage(entry, message) {
      if (typeof message === 'string') {
        entry.message = message;
      } else {
        entry = message;
      }
    },
    setAutopublishDate(date) {
      this.episode.publish.publish_date = date;
    },
    toggleIsFree() {
      this.episode.is_free = !this.episode.is_free;
    },
    togglePublished() {
      // Only allow users that can publish to toggle this.
      if (!this.canPublish) {
        return;
      }
      // Audio episodes must have a track before publishing.
      if (this.showAudioUpload && !this.episode.published && !this.hasAudioFile) {
        this.alertUploadButton = true;
        return;
      }

      this.episode.published = !this.episode.published;
    },
    removeUploadButtonAlert() {
      this.alertUploadButton = false;
    },
    deleteMessage(index) {
      if (confirm(this.$t('messages.confirmDeleteMessage'))) {
        this.messages.splice(index, 1);
        this.$root.$emit('messages-updated');
      }
    },
    setMessagePosition(position, message) {
      message.msgPosition = position;
      // Try to find a character to attach too
      const lastMessageInThisPosition = _.findLast(this.messages, (entry) => {
        return entry.msgPosition === position && entry.character_id;
      });

      if (lastMessageInThisPosition) {
        message.person = lastMessageInThisPosition.person;
        message.character_id = lastMessageInThisPosition.character_id;
      }
    },
    getDraggableData() {
      return {
        on: {
          change: () => {
            this.$root.$emit('messages-updated');
          },
        },
      };
    },
    setImage(image, index) {
      const reader = new FileReader();

      // Handle delete image
      if (!image) {
        return this.deleteImage(index);
      }

      reader.onload = (e) => {
        const url = `/docus/${this.snickId}/episodes/${this.episodeId}/image`;
        const picture = e.target.result;
        axios.post(url, {picture})
            .then((response) => {
              this.messages[index].image = response.data;
              //this.messages[index].message = null;
            })
            .catch((error) => {
              console.log(error);
            });
      };

      reader.readAsDataURL(image);
    },
    setCharacter(character, index) {
      this.messages[index].person = character.name;
      this.messages[index].character_id = character.id;
    },
    deleteImage(index) {
      this.messages[index].image = null;
    },
    saveEpisode() {
      this.errors = [];

      const url = `/docus/${this.snickId}/episodes/${this.episodeId}`;
      const data = {
        episode: this.episode,
        messages: this.messages.map((message, index) => {
          message.order = index;
          return message;
        }).filter((message) => {
          // Message must have a position
          if (message.msgPosition === null) return false;

          // A message must have a character, unless it is in the center
          if (message.person === null && message.msgPosition !== 'center') return false;

          // If it has an image, we don't expect text
          if (message.image !== null) return true;

          // If the text field is empty, remove it
          if (message.message.length === 0) return false;
          return true;
        }),
      };

      axios.put(url, data).then((response) => {
        this.notification = '✓ Saved';
        setTimeout(() => {
          this.notification = null
        }, 1500);
      }).catch((error) => {
        this.errors = error.response.data.errors;
      });
    },
    newEntry: function (e) {
      e.preventDefault();

      // Append entry
      this.messages.push({
        fullscreen: false,
        message: '',
        msgPosition: null,
        person: null,
        character_id: null,
        image: null,
        id: null,
      });

      this.scrollDown = true;
    },
    deleteEpisode: function (id) {
      const episodeUrl = `/docus/${this.snickId}/episodes/${this.episodeId}`;
      const redirectUrl = `/docus/${this.snickId}/edit`;

      if (!confirm(this.$t('messages.confirmDeleteEpisode'))) return false;

      axios.delete(episodeUrl).then(() => {
        window.location = redirectUrl;
      }).catch((error) => {
        console.log('Error removing episode');
      });
    },
    getErrors(key) {
      if (!this.errors) return false;

      return this.errors[key];
    },
    setErrorMessage(message) {
      this.errorMessage = message;
    },
    setSuccessMessage(message) {
      this.successMessage = message;
    }
  },
  i18n: {
    locale: 'en',
    messages: {
      en: {
        interface: {
          btnNew: 'New message',
        },
        labels: {
          published: 'Published',
          unpublihsed: 'Unpublished',
          audioTrack: 'Audio track',
        },
        messages: {
          confirmDeleteMessage: 'Are you sure you want to remove the message?',
          confirmDeleteEpisode: 'Are you sure you want to permanently delete the entire episode?',
        },
      },
      sv: {
        interface: {
          btnNew: 'Nytt meddelande',
        },
        labels: {
          published: 'Publicerad',
          unpublihsed: 'Opublicerad',
          audioTrack: 'Ljudspår',
        },
        messages: {
          confirmDeleteMessage: 'Är du säker på att du vill ta bort meddelandet?',
        },
      },
    },
  },
  data: function () {
    return {
      save_interval: 120,
      added: 0,
      episode: this.init.episode,
      messages: this.init.messages,
      extensions: this.init.extensions,
      notification: null,
      themes: [
        {'id': null, 'name': 'Use default theme'},
        ...this.init.themes
      ],
      emojiPicker: {
        id: null,
        style: {left: null, top: null},
        index: -1,
      },
      errors: {},
      scrollDown: false,
      alertUploadButton: false,
      errorMessage: false,
      successMessage: false,
    }
  },
  computed: {
    showAudioUpload: function () {
      // Show audio upload widget for snicks which has audio
      const types = this.episode.story.type.split('+');
      return types.indexOf('audio') >= 0;
    },
    showEditor: function () {
      // Do not show editor for "audio only" type of snicks
      return this.episode.story.type != 'audio';
    },
    published: function () {
      return this.episode.published
          ? this.$t('labels.published')
          : this.$t('labels.unpublihsed');
    },
    hasAudioFile: function () {
      return this.episode.duration > 0 && this.episode.audio_track.length > 0;
    },
    emojiStyles: function () {
      const app = document.getElementById('app').getBoundingClientRect();
      const component = this.$refs.component
          ? this.$refs.component.getBoundingClientRect()
          : {top: 0, left: 0};

      // TODO: Modify the code to compensate for this hard coded margin offset
      const margin = 48;

      // Offset top for the bottom messages
      const styles = {
        left: this.emojiPicker.left - component.left + margin + 'px',
      }

      const offset = this.messages.length - this.emojiPicker.index;
      if (offset <= 3) {
        styles.top = this.emojiPicker.top - app.top - 100 * (3 - offset + 1) + 'px';
      } else {
        styles.top = this.emojiPicker.top - app.top + 'px';
      }


      if (this.emojiPicker.index < 0) {
        styles.display = 'none';
      }
      return styles;
    }
  },
  updated() {
    // Scroll to bottom if scrollDown flag is set
    if (this.scrollDown === true) {
      window.scrollTo(0, document.body.scrollHeight);
      this.scrollDown = false;
    }
  },
  mounted() {
    // this.loadThemes();
    this.auto_save_timer = setInterval(() => {
      this.saveEpisode();
    }, 1000 * this.save_interval);
  }
};
</script>
<style>

</style>
